import { Typography, TypographyProps } from '@eventbrite/marmalade';
import React from 'react';
import { CamelVenue, EventLocation } from '../../types/events';

type LocationFormatFn = (
    venue?: CamelVenue,
    locations?: EventLocation[],
) => string;

const getFullLocationInfo: LocationFormatFn = (venue) => {
    if (venue?.name && venue?.address?.localizedAreaDisplay) {
        //ex. Eventbrite • San Francisco CA
        return `${venue?.name} \u2022 ${venue?.address?.localizedAreaDisplay}`;
    }

    return getVenueLocationInfo(venue);
};

const getVenueLocationInfo: LocationFormatFn = (venue) => {
    return venue?.name || '';
};

const getLocalLocationInfo: LocationFormatFn = (venue) => {
    return venue?.address?.localizedAreaDisplay || '';
};

const getLocationByType = (locations: EventLocation[] = [], type: string) =>
    locations.find((location) => location.type === type);

const getPlaceAndVenuLocationInfo: LocationFormatFn = (venue, locations) => {
    const venueName = getVenueLocationInfo(venue);

    const placeTypes = ['neighbourhood', 'borough', 'locality', 'region'];
    const place = placeTypes
        .map((type) => getLocationByType(locations, type))
        .find((location) => location);

    if (!place) {
        return venueName;
    } else if (!venue) {
        return place.name;
    } else {
        return `${place.name} · ${venueName}`;
    }
};

export type LocationFormatStyles =
    | 'full'
    | 'venue'
    | 'local'
    | 'place_and_venue';
const FORMAT_MAP = {
    full: getFullLocationInfo,
    venue: getVenueLocationInfo,
    local: getLocalLocationInfo,
    place_and_venue: getPlaceAndVenuLocationInfo,
};

/**
 * Extendable Utility to handle rendering location
 * information given a formatted Venue object.
 *
 * @param venue CamelVenue
 * @param style LocationFormatStyles: 'full' | 'venue' | 'local' | 'place_and_venue'
 * @returns
 */
export function useFormattedLocationString({
    venue,
    locations,
    style,
}: {
    venue?: CamelVenue;
    locations?: EventLocation[];
    style?: LocationFormatStyles;
}): string {
    const formatStyle = style ? style : 'full';

    const formatFn = React.useCallback(
        (venue?: CamelVenue, locations?: EventLocation[]) =>
            FORMAT_MAP[formatStyle]?.(venue, locations),
        [formatStyle],
    );

    const formattedLocationString = React.useMemo(() => {
        return formatFn(venue, locations);
    }, [formatFn, venue, locations]);

    return formattedLocationString;
}

interface EventCardLocationProps
    extends Omit<TypographyProps, 'children' | 'style'> {
    formattedLocationString?: string;
}

export const EventCardLocation: React.FunctionComponent<
    EventCardLocationProps
> = (props) => {
    const { formattedLocationString, ...typographyProps } = props;

    if (!props.formattedLocationString) {
        return null;
    }

    return (
        <Typography {...typographyProps}>{formattedLocationString}</Typography>
    );
};
